/**
 * Infinite Scroll for Shopify Collection Pages — v4
 * Automatically loads ALL pages of products when scrolling.
 * Works with Dawn's facets.js fetch-based pagination.
 *
 * Fixes v4:
 * - Use Section Rendering API (section_id) for lighter, more reliable fetches
 * - Scroll-based fallback trigger if IntersectionObserver doesn't fire
 * - Precise grid-item selector (:scope > .grid__item) to avoid nested matches
 * - Retry on network errors (up to 3 attempts with back-off)
 * - Duplicate page prevention via loaded-URLs set
 * - Facets MutationObserver inside DOMContentLoaded
 */
(function () {
  'use strict';

  const GRID_SELECTOR = '#product-grid';
  const PAGINATION_SELECTOR = '.pagination-wrapper';
  const NEXT_LINK_SELECTOR = '.pagination__item--next.pagination__item-arrow';
  const LOAD_THRESHOLD = 600; // px before bottom to trigger
  const MAX_RETRIES = 3;
  const RETRY_DELAY_MS = 1500; // base delay; doubles each retry

  let isLoading = false;
  let noMorePages = false;
  let observer = null;
  let retryCount = 0;
  const loadedUrls = new Set(); // prevent loading same page twice

  /* ── Utilities ─────────────────────────────────────────── */
  function debounce(fn, delay) {
    let timer;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(fn, delay);
    };
  }

  /* ── Bootstrap ─────────────────────────────────────────── */
  function init() {
    const grid = document.querySelector(GRID_SELECTOR);
    if (!grid) return;

    // Track initial page
    loadedUrls.add(normalizeUrl(window.location.href));

    document.body.classList.add('io-infinite-scroll-active');
    createSentinel(grid);
  }

  /* ── Sentinel (IntersectionObserver) ───────────────────── */
  function createSentinel(grid) {
    const existing = document.getElementById('io-scroll-sentinel');
    if (existing) existing.remove();

    const sentinel = document.createElement('div');
    sentinel.id = 'io-scroll-sentinel';
    sentinel.style.height = '1px';

    const paginationWrapper = document.querySelector(PAGINATION_SELECTOR);
    if (paginationWrapper) {
      paginationWrapper.parentNode.insertBefore(sentinel, paginationWrapper.nextSibling);
    } else {
      grid.parentNode.appendChild(sentinel);
    }

    if (observer) observer.disconnect();

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isLoading && !noMorePages) {
            loadNextPage();
          }
        });
      },
      { rootMargin: `0px 0px ${LOAD_THRESHOLD}px 0px` }
    );

    observer.observe(sentinel);
  }

  /* ── Helpers ───────────────────────────────────────────── */
  function normalizeUrl(url) {
    try {
      const u = new URL(url, window.location.origin);
      // Keep only pathname + search (ignore hash, strip section_id)
      u.searchParams.delete('section_id');
      return u.pathname + u.search;
    } catch (_) {
      return url;
    }
  }

  function getNextPageUrl() {
    // Primary: the "next" arrow link
    const nextLink = document.querySelector(
      PAGINATION_SELECTOR + ' ' + NEXT_LINK_SELECTOR
    );
    if (nextLink && nextLink.href) return nextLink.href;

    // Fallback: pagination link with current_page + 1
    const currentPage = document.querySelector('.pagination__item--current');
    if (currentPage) {
      const currentNum = parseInt(currentPage.textContent.trim(), 10);
      const allLinks = document.querySelectorAll('.pagination__item.link');
      for (const link of allLinks) {
        const num = parseInt(link.textContent.trim(), 10);
        if (num === currentNum + 1) return link.href;
      }
    }

    return null;
  }

  function getSectionUrl(pageUrl) {
    var grid = document.querySelector(GRID_SELECTOR);
    var sectionId = grid ? grid.dataset.id : null;
    if (!sectionId) return pageUrl;

    try {
      var u = new URL(pageUrl, window.location.origin);
      u.searchParams.set('section_id', sectionId);
      return u.toString();
    } catch (_) {
      return pageUrl;
    }
  }

  /* ── Loader UI ─────────────────────────────────────────── */
  function showLoader() {
    let loader = document.getElementById('io-infinite-loader');
    if (!loader) {
      loader = document.createElement('div');
      loader.id = 'io-infinite-loader';
      loader.className = 'io-infinite-loader';
      loader.innerHTML = '<div class="spinner"></div>';
      const grid = document.querySelector(GRID_SELECTOR);
      if (grid) grid.parentNode.appendChild(loader);
    }
    loader.style.display = 'flex';
  }

  function hideLoader() {
    const loader = document.getElementById('io-infinite-loader');
    if (loader) loader.style.display = 'none';
  }

  /* ── Core page loader ─────────────────────────────────── */
  function loadNextPage() {
    const nextUrl = getNextPageUrl();
    if (!nextUrl) {
      noMorePages = true;
      return;
    }

    // Duplicate guard
    const normalized = normalizeUrl(nextUrl);
    if (loadedUrls.has(normalized)) {
      noMorePages = true;
      return;
    }

    isLoading = true;
    showLoader();

    // Use Section Rendering API for lighter, more reliable responses
    const fetchUrl = getSectionUrl(nextUrl);

    fetch(fetchUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((html) => {
        retryCount = 0; // reset on success
        loadedUrls.add(normalized);

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newGrid = doc.querySelector(GRID_SELECTOR);
        const newPagination = doc.querySelector(PAGINATION_SELECTOR);

        if (newGrid) {
          const grid = document.querySelector(GRID_SELECTOR);
          const items = newGrid.querySelectorAll(':scope > .grid__item');
          items.forEach((item) => {
            item.style.setProperty('--animation-order', grid.children.length);
            grid.appendChild(item);
          });
        }

        // Swap pagination so next-page link updates
        const currentPagination = document.querySelector(PAGINATION_SELECTOR);
        if (newPagination && currentPagination) {
          currentPagination.innerHTML = newPagination.innerHTML;
        } else if (!newPagination && currentPagination) {
          currentPagination.remove();
          noMorePages = true;
        }

        // Prepare for next page
        if (!noMorePages) {
          const grid = document.querySelector(GRID_SELECTOR);
          if (grid) createSentinel(grid);
        }

        hideLoader();
        isLoading = false;
      })
      .catch((err) => {
        console.warn('[IO Infinite Scroll] Error loading page:', err.message);
        hideLoader();
        isLoading = false;

        // Retry with exponential back-off
        if (retryCount < MAX_RETRIES) {
          retryCount++;
          const delay = RETRY_DELAY_MS * Math.pow(2, retryCount - 1);
          console.info(`[IO Infinite Scroll] Retrying (${retryCount}/${MAX_RETRIES}) in ${delay}ms…`);
          setTimeout(() => loadNextPage(), delay);
        } else {
          console.error('[IO Infinite Scroll] Max retries reached. Restoring pagination.');
          // Show standard pagination so the user can still navigate
          document.body.classList.remove('io-infinite-scroll-active');
          noMorePages = true;
        }
      });
  }

  /* ── Scroll fallback ──────────────────────────────────── */
  function onScrollFallback() {
    if (isLoading || noMorePages) return;
    const sentinel = document.getElementById('io-scroll-sentinel');
    if (!sentinel) return;
    const rect = sentinel.getBoundingClientRect();
    if (rect.top < window.innerHeight + LOAD_THRESHOLD) {
      loadNextPage();
    }
  }

  /* ── Init & facets watcher ────────────────────────────── */
  function onReady() {
    init();

    // Scroll-based fallback in case IntersectionObserver doesn't trigger
    window.addEventListener('scroll', debounce(onScrollFallback, 200), { passive: true });

    // Re-init when facets.js replaces the grid (AJAX filtering)
    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) {
      new MutationObserver(function () {
        isLoading = false;
        noMorePages = false;
        retryCount = 0;
        loadedUrls.clear();
        loadedUrls.add(normalizeUrl(window.location.href));
        hideLoader();
        setTimeout(init, 100);
      }).observe(gridContainer, { childList: true, subtree: false });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
})();
